// Datentypen und Edit-Logik des Tour-Stopovers — bewusst außerhalb der Komponenten-Datei,
// damit Klassen und Modelle sie ohne Komponenten-Import (und damit ohne Import-Zyklus) nutzen können
import type { PAOperation } from "../../classes/operation";
import type { PATechnicianDate } from "../../classes/technician-date";
import type { MapContainer } from "../../map/pa-map";
import { PATourPlannerControl } from "../../singletons/pa-tourplanner-control";

export interface OperationWithStore {
  id: number;
  readonly storeId: number;
  readonly done?: boolean;
  readonly started?: boolean;
}

export interface TourIdxShowTour {
  operations: OperationWithStore[];
  getColor(): string;
}

export interface TourIdxEditTour extends TourIdxShowTour {
  technicianDate: PATechnicianDate;
}

export type TourIdxView = TourIdxShowView | TourIdxEditView;

export interface TourIdxData {
  idx: number;
  color: string;
  operation_id: number;
  done?: boolean;
  started?: boolean;
}

export type ShowTourIdx = { tour: TourIdxShowTour } & TourIdxData;
export type EditTourIdx = { tour: TourIdxEditTour } & TourIdxData;

export interface TourIdxShowView {
  type: "show";
  tour_idxs: ShowTourIdx[];
  referenceTours: TourIdxShowTour[];
}

export interface TourIdxEditView {
  type: "edit";
  tour_idxs: EditTourIdx[];
  tourStopoverEditModule: TourStopoverEditModule;
  referenceTours: TourIdxEditTour[];
}

export class TourStopoverEditModule {
  private _notInsertedOperations: PAOperation[] = [];
  private _deletedOperations: PAOperation[] = [];

  constructor(
    private _mapContainer: MapContainer,
    private _selectedTechnicianDate: PATechnicianDate,
    private _storeId: number,
    private _referenceTours: TourIdxEditTour[],
  ) {}

  shownNotInsertedOperations: PAOperation[] = [];
  shownDeletedOperations: PAOperation[] = [];

  get storeId(): number {
    return this._storeId;
  }

  set storeId(id: number) {
    this._storeId = id;
  }

  get referenceTours(): TourIdxEditTour[] {
    return this._referenceTours;
  }

  set referenceTours(ref_tours: TourIdxEditTour[]) {
    this._referenceTours = ref_tours;
  }

  get selectedTechnicianDate(): PATechnicianDate {
    return this._selectedTechnicianDate;
  }

  set selectedTechnicianDate(td: PATechnicianDate) {
    this._selectedTechnicianDate = td;
    this.updateShownNotInsertedOperations();
    this.updateShownDeletedOperations();
  }

  get notInsertedOperations(): PAOperation[] {
    return this._notInsertedOperations;
  }

  set notInsertedOperations(operations: PAOperation[]) {
    this._notInsertedOperations = operations;
    this.updateShownNotInsertedOperations();
  }

  get deletedOperations(): PAOperation[] {
    return this._deletedOperations;
  }

  set deletedOperations(operations: PAOperation[]) {
    this._deletedOperations = operations;
    this.updateShownDeletedOperations();
  }

  get mapContainer(): MapContainer {
    return this._mapContainer;
  }

  set mapContainer(map_container: MapContainer) {
    this._mapContainer = map_container;
  }

  clickIdx(tour_idx: EditTourIdx) {
    if (!this.isMainMapContainer()) {
      tour_idx.tour.technicianDate.removeOperationsFromNewTour([tour_idx.operation_id]);
    } else {
      PATourPlannerControl.Instance.showTourOverview(tour_idx.tour.technicianDate);
    }
  }

  isMainMapContainer(): boolean {
    return this._mapContainer.name == "map_0";
  }

  makeNotInsertedOperationsUnremovable() {
    if (this._selectedTechnicianDate) {
      for (const operation of this.shownNotInsertedOperations) {
        const store = operation.getStore();
        if (this.storeId == store.id) {
          this._selectedTechnicianDate.insertionConfigMap.get(operation.id).removable = false;
          this._selectedTechnicianDate.updateNewTourConfig();
        }
      }
    }
  }

  addStoreOperationsToNewTour() {
    if (this._selectedTechnicianDate) {
      for (const operation of this.shownDeletedOperations) {
        const store = operation.getStore();
        if (this.storeId == store.id) {
          this._selectedTechnicianDate.restoreOperationToNewTour(operation);
        }
      }
    }
  }

  private updateShownNotInsertedOperations() {
    if (this.selectedTechnicianDate) {
      this.shownNotInsertedOperations = this._notInsertedOperations.filter((op) =>
        this.selectedTechnicianDate.changedTourContainer.unfittingOperations.includes(op),
      );
    } else {
      this.shownNotInsertedOperations = [];
    }
  }

  private updateShownDeletedOperations() {
    if (this.selectedTechnicianDate) {
      this.shownDeletedOperations = this._deletedOperations.filter((op) =>
        this.selectedTechnicianDate.changedTourContainer.deletingOperations.includes(op),
      );
    } else {
      this.shownDeletedOperations = [];
    }
  }
}
